/**
 * Renders one ownership group in the Vault catalog.
 */
import type { ReactNode } from 'react'
import {
  Building2,
  ChevronRight,
  Database,
  UserRound,
} from 'lucide-react'

import type { VaultCatalogEntry, VaultRecord, VaultType } from '../types'

export function VaultCatalogSection({
  type,
  label,
  description,
  entries,
  workspaceNames,
  inboundConnectorCounts,
  onOpen,
  children,
}: {
  type: VaultType
  label: string
  description: string
  entries: VaultCatalogEntry[]
  workspaceNames: Record<string, string>
  inboundConnectorCounts: Record<string, number | undefined>
  onOpen: (vault: VaultRecord) => void
  children?: ReactNode
}) {
  return (
    <section className="flex flex-col gap-2" aria-labelledby={`vault-group-${type}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h2
          id={`vault-group-${type}`}
          className="text-[11px] font-[650] uppercase tracking-[0.06em] text-slate-400"
        >
          {label}
        </h2>
        <span className="rounded-full bg-slate-100 px-[7px] py-px text-[10.5px] font-[650] text-slate-500">
          {entries.length}
        </span>
        <p className="ml-auto text-[11.5px] text-slate-300">{description}</p>
      </div>
      {entries.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {entries.map(entry => (
            <VaultCard
              key={entry.vault.vaultId}
              entry={entry}
              workspaceNames={workspaceNames}
              connectorCount={inboundConnectorCounts[entry.vault.vaultId]}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
      {children}
    </section>
  )
}

function VaultCard({
  entry,
  workspaceNames,
  connectorCount,
  onOpen,
}: {
  entry: VaultCatalogEntry
  workspaceNames: Record<string, string>
  connectorCount?: number
  onOpen: (vault: VaultRecord) => void
}) {
  const { vault, pairing } = entry
  const Icon = vault.vaultType === 'user'
    ? UserRound
    : vault.vaultType === 'paired' ? Database : Building2
  const iconClass = vault.vaultType === 'user'
    ? 'bg-violet-50 text-violet-600'
    : vault.vaultType === 'paired'
      ? 'bg-indigo-50 text-indigo-600'
      : 'bg-teal-50 text-teal-600'
  const workspaceName = pairing
    ? workspaceNames[pairing.workspaceId] ?? pairing.workspaceId
    : undefined

  return (
    <button
      type="button"
      className="flex min-w-0 items-center gap-3 rounded-[11px] border border-slate-200 bg-transparent px-4 py-3.5 text-left transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50"
      onClick={() => onOpen(vault)}
    >
      <span className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] ${iconClass}`}>
        <Icon className="h-[17px] w-[17px]" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-[650] text-slate-900">
          {vault.name}
        </span>
        <span className="block truncate text-[12px] text-slate-500">
          {vault.vaultType === 'user'
            ? 'Personal files and drafts · upload only'
            : vault.vaultType === 'paired' && workspaceName
              ? `Paired to ${workspaceName}${connectorCount === undefined ? '' : ` · ${connectorCount} ${connectorCount === 1 ? 'connector' : 'connectors'}`}`
              : vault.description || 'Shared departmental content'}
        </span>
      </span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
    </button>
  )
}
